<script setup lang="ts">
import type { FrontContent, ScadenzarioElettorale } from "@gasparigit/omnia-sdk";
import { google, ics, office365, outlook } from "calendar-link";
import { createEvent, link } from "~/lib/utils/contents";

const content = defineModel<ScadenzarioElettorale>("content", { required: true });
</script>

<template>
  <div class="main-content mb-10" v-if="content.scadenze_elettorali?.length">
    <div class="flex items-center justify-between gap-3">
      <h2 class="text-3xl font-extrabold">Scadenze</h2>
      <Button>
        <NuxtLink :href="content.download_pdf_url">Stampa</NuxtLink>
      </Button>
    </div>
    <table class="mt-8">
      <template v-for="(scadenzaElettorale, i) in content.scadenze_elettorali" :key="`scadenza-elettorale-${scadenzaElettorale.id}`">
        <tr class="group align-top grid md:table-row mb-10 border-l-2 md:border-none pl-3 md:p-0 border-primary">
          <td>
            <div v-if="scadenzaElettorale.date && (i <= 0 || content.scadenze_elettorali.at(i - 1)?.date.toString() !== scadenzaElettorale.date.toString())" class="flex flex-col gap-3 text-base">
              <div class="font-monospace flex flex-col text-tertiary md:items-end font-semibold leading-none">
                <span>{{ scadenzaElettorale.date.toLocaleString("default", { weekday: "long" }) }}</span>
                <span class="text-4xl md:text-3xl font-bold">{{ scadenzaElettorale.date.getDate() }}</span>
                <span class="whitespace-nowrap">{{ scadenzaElettorale.date.toLocaleString("default", { month: "long", year: "numeric" }) }}</span>
              </div>
            </div>
          </td>
          <td class="md:pl-5 group-last:pb-0 text-base md:border-r-2 md:pr-10 relative pb-5 md:pb-12 md:group-last:pb-0">
            <template v-if="scadenzaElettorale.date && (i <= 0 || content.scadenze_elettorali.at(i - 1)?.date.toString() !== scadenzaElettorale.date.toString())">
              <span
                class="hidden md:flex absolute font-semibold text-base text-primary rounded-full w-8 h-8 border-[3px] border-primary items-center justify-center -right-4 top-5 bg-background leading-none"
              >
                {{ scadenzaElettorale.date.getDate() }}
              </span>
              <div class="text-muted-foreground md:text-right flex flex-col md:items-end md:gap-3">
                <div>
                  <span v-html="scadenzaElettorale.string_relative_to_poster_date"></span>
                </div>
                <div>
                  <span v-html="scadenzaElettorale.string_relative_to_election_date"></span>
                </div>
              </div>
            </template>
          </td>
          <td class="md:pl-10 md:pb-12 group-last:pb-0">
            <div class="grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-10">
              <div class="flex flex-col col-span-3">
                <div class="prose prose-a:text-primary prose-p:my-2 max-w-full -mt-2" v-html="scadenzaElettorale.body"></div>
                <div>
                  <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                      <Button variant="link" class="flex items-center justify-start text-base font-semibold text-themeGray hover:text-primary transition-colors">
                        <Icon name="carbon:calendar-add" class="w-5 h-5 me-1" />
                        <span>Aggiungi al calendario</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center">
                      <DropdownMenuGroup>
                        <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="google(createEvent(scadenzaElettorale))">Google</NuxtLink></DropdownMenuItem>
                        <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="outlook(createEvent(scadenzaElettorale))">Outlook</NuxtLink></DropdownMenuItem>
                        <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="office365(createEvent(scadenzaElettorale))">Office 365</NuxtLink></DropdownMenuItem>
                        <DropdownMenuItem><NuxtLink target="_blank" class="w-full" :href="ics(createEvent(scadenzaElettorale))">File ICS (Apple)</NuxtLink></DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div class="flex flex-col gap-3" v-if="scadenzaElettorale.related_contents?.length || scadenzaElettorale.files?.length">
                <h4 class="font-bold text-xl">Riferimenti</h4>
                <div class="grid grid-cols-1 gap-3 mt-2">
                  <div v-for="pratica in scadenzaElettorale.related_contents?.filter((content) => content.contentable_type === 'pratica')" :key="`pratica-${pratica.id}`">
                    <Icon name="carbon:folder" class="opacity-70 me-2 -mt-1" />
                    <NuxtLink :href="link({ content: pratica as unknown as FrontContent })" class="link">{{ pratica.title }}</NuxtLink>
                  </div>
                  <div v-for="document in scadenzaElettorale.files" :key="`file-${document.id}`">
                    <Icon name="carbon:document" class="opacity-70 me-2 -mt-1" />
                    <NuxtLink :href="document.file?.download_url" class="link">{{ document.title }}</NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </table>
  </div>
</template>
